import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../components/Layout';
import {
  Database,
  Download,
  Upload,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { backupAPI } from '../utils/api';
import api from '../utils/api';

const DatabaseBackup = () => {
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [password, setPassword] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // Check if user is super-admin
  useEffect(() => {
    if (user?.role !== 'super-admin') {
      toast.error('Access denied. Only super administrators can access this page.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch backups on mount
  useEffect(() => {
    if (user?.role === 'super-admin') {
      fetchBackups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await backupAPI.list();
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch backups');
    }
  };

  const handleCreateBackup = () => {
    setModalAction('create');
    setSelectedBackup(null);
    setShowPasswordModal(true);
  };

  const handleRestoreBackup = backup => {
    setModalAction('restore');
    setSelectedBackup(backup);
    setShowPasswordModal(true);
  };

  const handleDeleteBackup = backup => {
    setModalAction('delete');
    setSelectedBackup(backup);
    setShowPasswordModal(true);
  };

  const handleDownloadBackup = async backup => {
    try {
      const response = await api.get(`/backup/download/${backup.filename}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(error.response?.data?.message || 'Failed to download backup');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json.gz') && !file.name.endsWith('.gz')) {
        toast.error('Please select a valid backup file (.json.gz)');
        return;
      }
      setUploadFile(file);
      setModalAction('upload');
      setShowPasswordModal(true);
    }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile || !password) {
      toast.error('Please select a file and enter your password');
      return;
    }

    setLoading(true);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async e => {
        try {
          const base64Data = btoa(
            new Uint8Array(e.target.result).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );

          const response = await api.post('/backup/upload', {
            password,
            backupData: base64Data,
          });

          toast.success(
            response.data.message || 'Database restored successfully from uploaded backup'
          );
          setShowPasswordModal(false);
          setPassword('');
          setUploadFile(null);
          setModalAction(null);

          // Clear authentication and redirect to login
          localStorage.clear();
          sessionStorage.clear();

          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
        } catch (error) {
          console.error('Error uploading backup:', error);
          toast.error(error.response?.data?.message || 'Failed to restore from backup');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(uploadFile);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read backup file');
      setLoading(false);
    }
  };

  const executeAction = async () => {
    if (modalAction === 'upload') {
      await handleUploadRestore();
      return;
    }

    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      if (modalAction === 'create') {
        await backupAPI.create(password);
        toast.success('Database backup created successfully');
        fetchBackups();
      } else if (modalAction === 'restore') {
        const response = await backupAPI.restore(password, selectedBackup.filename);
        toast.success(response.data.message || 'Database restored successfully');

        // Clear authentication and redirect to login
        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else if (modalAction === 'delete') {
        await backupAPI.delete(selectedBackup.filename, password);
        toast.success('Backup deleted successfully');
        fetchBackups();
      }

      setShowPasswordModal(false);
      setPassword('');
      setModalAction(null);
      setSelectedBackup(null);
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowPasswordModal(false);
    setPassword('');
    setModalAction(null);
    setSelectedBackup(null);
    setUploadFile(null);
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getModalTitle = () => {
    if (modalAction === 'create') return 'Create Database Backup';
    if (modalAction === 'restore') return 'Restore Database';
    if (modalAction === 'delete') return 'Delete Backup';
    if (modalAction === 'upload') return 'Restore from Local Backup';
    return '';
  };

  const getModalMessage = () => {
    if (modalAction === 'create') {
      return 'Enter your password to create a new database backup. This will create a compressed backup file of the entire database.';
    }
    if (modalAction === 'restore') {
      return `Enter your password to restore the database from backup "${selectedBackup?.filename}". WARNING: This will replace all current data with the backup data. All users will be logged out automatically.`;
    }
    if (modalAction === 'delete') {
      return `Enter your password to delete the backup "${selectedBackup?.filename}". This action cannot be undone.`;
    }
    if (modalAction === 'upload') {
      return `Enter your password to restore the database from "${uploadFile?.name}". WARNING: This will replace all current data with the backup data. All users will be logged out automatically.`;
    }
    return '';
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Database className="mr-3 h-8 w-8 text-blue-600" />
                Database Backup
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Create, restore, and manage database backups
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleUploadClick}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="mr-2 h-4 w-4" />
                Restore from Local
              </button>
              <button
                onClick={handleCreateBackup}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="mr-2 h-4 w-4" />
                Create Backup
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".gz,.json.gz"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>

        {/* Warning Banner */}
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Important:</strong> Database operations require your password for security.
                Restoring a backup will replace all current data. Always create a backup before
                restoring.
              </p>
            </div>
          </div>
        </div>

        {/* Backups List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Available Backups</h3>
            <p className="mt-1 text-sm text-gray-500">
              {backups.length} backup{backups.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {backups.length === 0 ? (
            <div className="text-center py-12">
              <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No backups</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first database backup.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {backups.map((backup, index) => (
                <li key={index} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <Database className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {backup.filename}
                          </p>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {formatDate(backup.created)}
                            <span className="mx-2">•</span>
                            <HardDrive className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {backup.size}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDownloadBackup(backup)}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title="Download to local"
                      >
                        <Download className="mr-1.5 h-3.5 w-3.5" />
                        Download
                      </button>
                      <button
                        onClick={() => handleRestoreBackup(backup)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title="Restore from server"
                      >
                        <Upload className="mr-1.5 h-3.5 w-3.5" />
                        Restore
                      </button>
                      <button
                        onClick={() => handleDeleteBackup(backup)}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Delete from server"
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeModal}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div
                  className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${
                    modalAction === 'delete'
                      ? 'bg-red-100'
                      : modalAction === 'restore' || modalAction === 'upload'
                        ? 'bg-yellow-100'
                        : 'bg-blue-100'
                  }`}
                >
                  {modalAction === 'delete' ? (
                    <Trash2 className={`h-6 w-6 text-red-600`} />
                  ) : modalAction === 'restore' || modalAction === 'upload' ? (
                    <AlertTriangle className={`h-6 w-6 text-yellow-600`} />
                  ) : (
                    <CheckCircle className={`h-6 w-6 text-blue-600`} />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">{getModalTitle()}</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{getModalMessage()}</p>
                  </div>
                  <div className="mt-4">
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoFocus
                      className="shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full px-4 py-3 text-base border border-gray-300 rounded-md placeholder-gray-400 transition-colors"
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          executeAction();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  disabled={loading}
                  onClick={executeAction}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:col-start-2 sm:text-sm ${
                    modalAction === 'delete'
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Processing...' : 'Confirm'}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DatabaseBackup;
